import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import type { AffectedExistingBooking } from "../../features/Booking-Form/typings/cancel-existing-booking.types";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";

/**
 * Asks before a new booking cancels or shortens bookings the person already has.
 * Shared by the booking form and the colleague floor.
 */
type CancelExistingBookingsDialogProps = {
  /** Whether the question is on screen. */
  open: boolean;
  /** Bookings the server would cancel or shorten for this new booking. */
  affectedBookings: AffectedExistingBooking[];
  /** True while the confirmed save is in flight. */
  confirming: boolean;
  /** Save the new booking and apply the listed changes. */
  onProceed: () => void;
  /** Leave the existing bookings unchanged. */
  onDismiss: () => void;
};

function formatAffectedBookingRange(
  booking: AffectedExistingBooking,
  dateTimeFormat: string
): string {
  const start = DateTime.fromISO(booking.start);
  const end = DateTime.fromISO(booking.end);
  if (start.isValid && end.isValid) {
    return `${start.toFormat(dateTimeFormat)} – ${end.toFormat(dateTimeFormat)}`;
  }
  return booking.label ?? `#${booking.id}`;
}

export function CancelExistingBookingsDialog({
  open,
  affectedBookings,
  confirming,
  onProceed,
  onDismiss
}: Readonly<CancelExistingBookingsDialogProps>) {
  const { t } = useTranslation();
  const { dateFormat, timeFormat } = useTimeFormat();
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;

  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      aria-labelledby="cancel-existing-dialog-title"
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}
    >
      <DialogTitle id="cancel-existing-dialog-title">
        {t("_bookingConfirm_cancelExistingTitle")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("_bookingConfirm_cancelExistingMessage")}
        </DialogContentText>
        <Table size="small" sx={{ "& td, & th": { borderBottom: 1, borderColor: "divider" } }}>
          <TableHead>
            <TableRow>
              <TableCell component="th" variant="head">
                {t("Booking")}
              </TableCell>
              <TableCell component="th" variant="head">
                {t("Date and Time")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affectedBookings.map(booking => (
              <TableRow key={booking.id}>
                <TableCell>
                  {booking.label ?? t(booking.bookingType)}
                  {booking.action === "shorten"
                    ? ` (${t("_bookingConfirm_cancelExistingShorten")})`
                    : ""}
                </TableCell>
                <TableCell>{formatAffectedBookingRange(booking, dateTimeFormat)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onDismiss}
          color="secondary"
          data-testid="cancel-existing-dialog-dismiss-btn"
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={onProceed}
          color="primary"
          variant="contained"
          loading={confirming}
          data-testid="cancel-existing-dialog-proceed-btn"
        >
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
